import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { AlertTriangle, CalendarDays, CheckCircle2, Loader2, RefreshCw, Save, Search, Users } from 'lucide-react';
import { db } from '../../firebase/config';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const today = new Date();
const currentMonth = MONTHS[today.getMonth()];
const currentYear = today.getFullYear();

const toInt = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const percent = (attended, conducted) => (
  conducted > 0 ? Number(((attended / conducted) * 100).toFixed(2)) : 0
);

const studentName = (student) => (
  `${student.firstName || ''} ${student.surname || ''}`.trim() ||
  student.name ||
  student.rollNumber
);

const cleanInput = (value) => {
  if (value === '') return '';
  return String(Math.max(0, toInt(value)));
};

const periodKey = ({ month, year, fromDate, toDate }) => (
  `${year}-${month}-${fromDate || 'start'}-${toDate || 'end'}`.replace(/[^a-z0-9_-]/gi, '_')
);

const getPeriodSource = (attendance, period) => {
  if (!attendance) return null;

  const entries = Array.isArray(attendance.entries) ? attendance.entries : [];
  const exactPeriod = entries.find((entry) => entry.id === periodKey(period));
  if (exactPeriod) return exactPeriod;

  const sameMonth = attendance.month === period.month &&
    String(attendance.year || period.year) === String(period.year);

  return sameMonth ? attendance : null;
};

const makeRow = (student, attendance, period) => {
  const source = getPeriodSource(attendance, period);
  const theoryTotal = source?.theoryTotal || {};
  const practicalTotal = source?.practicalTotal || {};

  return {
    rollNumber: student.rollNumber,
    name: studentName(student),
    division: student.division || '',
    batch: student.batch || '',
    theoryConducted: theoryTotal.conducted ?? '',
    theoryAttended: theoryTotal.attended ?? '',
    practicalConducted: practicalTotal.conducted ?? '',
    practicalAttended: practicalTotal.attended ?? '',
    existingAttendance: attendance || null,
    dirty: false
  };
};

const buildAttendanceRecord = (row, period, userName) => {
  const theoryConducted = toInt(row.theoryConducted);
  const theoryAttended = toInt(row.theoryAttended);
  const practicalConducted = toInt(row.practicalConducted);
  const practicalAttended = toInt(row.practicalAttended);
  const totalLectures = theoryConducted + practicalConducted;
  const attendedLectures = theoryAttended + practicalAttended;
  const theoryPercentage = percent(theoryAttended, theoryConducted);
  const practicalPercentage = percent(practicalAttended, practicalConducted);
  const overallPercentage = percent(attendedLectures, totalLectures);
  const entryId = periodKey(period);
  const entry = {
    id: entryId,
    month: period.month,
    year: Number(period.year),
    fromDate: period.fromDate,
    toDate: period.toDate,
    theoryTotal: { conducted: theoryConducted, attended: theoryAttended },
    practicalTotal: { conducted: practicalConducted, attended: practicalAttended },
    totalLectures,
    attendedLectures,
    theoryPercentage,
    practicalPercentage,
    overallPercentage,
    updatedAt: serverTimestamp(),
    updatedBy: userName || 'Class Coordinator'
  };
  const previousEntries = Array.isArray(row.existingAttendance?.entries)
    ? row.existingAttendance.entries
    : [];

  return {
    ...(row.existingAttendance || {}),
    rollNumber: row.rollNumber,
    studentName: row.name,
    division: row.division,
    batch: row.batch,
    month: period.month,
    year: Number(period.year),
    fromDate: period.fromDate,
    toDate: period.toDate,
    dateRange: {
      fromDate: period.fromDate,
      toDate: period.toDate
    },
    totalLectures,
    attendedLectures,
    percentage: overallPercentage,
    overallPercentage,
    theoryPercentage,
    practicalPercentage,
    theoryTotal: entry.theoryTotal,
    practicalTotal: entry.practicalTotal,
    courses: [
      {
        code: 'THEORY',
        name: 'Theory Attendance',
        type: 'theory',
        conducted: theoryConducted,
        attended: theoryAttended
      },
      {
        code: 'PRACTICAL',
        name: 'Practical Attendance',
        type: 'practical',
        conducted: practicalConducted,
        attended: practicalAttended
      }
    ],
    entries: [
      entry,
      ...previousEntries.filter((item) => item.id !== entryId)
    ].slice(0, 24),
    updatedAt: serverTimestamp(),
    updatedBy: userName || 'Class Coordinator'
  };
};

const AttendanceEntryWidget = ({ assignedDivision, userName }) => {
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [bulkTheory, setBulkTheory] = useState('');
  const [bulkPractical, setBulkPractical] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const period = useMemo(() => ({
    month,
    year,
    fromDate,
    toDate
  }), [fromDate, month, toDate, year]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');

      const studentsRef = collection(db, 'students');
      const studentsQuery = assignedDivision
        ? query(studentsRef, where('division', '==', assignedDivision), orderBy('rollNumber', 'asc'))
        : query(studentsRef, orderBy('rollNumber', 'asc'));
      const studentsSnapshot = await getDocs(studentsQuery);
      const loadedStudents = studentsSnapshot.docs.map((studentDoc) => studentDoc.data());

      const nextRows = await Promise.all(loadedStudents.map(async (student) => {
        const rollNumber = student.rollNumber;
        const attendanceDoc = await getDoc(doc(db, 'attendance', rollNumber));
        return makeRow(student, attendanceDoc.exists() ? attendanceDoc.data() : null, period);
      }));

      setRows(nextRows);
    } catch (loadError) {
      console.error('Error loading attendance entry rows:', loadError);
      setError('Could not load attendance rows.');
    } finally {
      setLoading(false);
    }
  }, [assignedDivision, period]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const updateRow = (rollNumber, field, value) => {
    setRows((currentRows) => currentRows.map((row) => (
      row.rollNumber === rollNumber
        ? { ...row, [field]: cleanInput(value), dirty: true }
        : row
    )));
  };

  const applyConductedToAll = () => {
    setRows((currentRows) => currentRows.map((row) => ({
      ...row,
      theoryConducted: bulkTheory === '' ? row.theoryConducted : cleanInput(bulkTheory),
      practicalConducted: bulkPractical === '' ? row.practicalConducted : cleanInput(bulkPractical),
      dirty: true
    })));
  };

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) => (
      row.rollNumber.toLowerCase().includes(needle) ||
      row.name.toLowerCase().includes(needle) ||
      row.batch.toLowerCase().includes(needle)
    ));
  }, [rows, search]);

  const invalidRows = rows.filter((row) => (
    toInt(row.theoryAttended) > toInt(row.theoryConducted) ||
    toInt(row.practicalAttended) > toInt(row.practicalConducted)
  ));

  const dateRangeInvalid = Boolean(fromDate && toDate && fromDate > toDate);
  const canSave = rows.length > 0 && !saving && !loading && invalidRows.length === 0 && !dateRangeInvalid;

  const saveRows = async () => {
    if (!canSave) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const batch = writeBatch(db);
      rows.forEach((row) => {
        const attendanceRecord = buildAttendanceRecord(row, period, userName);
        batch.set(doc(db, 'attendance', row.rollNumber), attendanceRecord, { merge: true });
      });

      await batch.commit();

      const savedRows = rows.map((row) => ({
        ...row,
        existingAttendance: buildAttendanceRecord(row, period, userName),
        dirty: false
      }));

      setRows(savedRows);
      setMessage(`Saved attendance for ${rows.length} student${rows.length === 1 ? '' : 's'}.`);
    } catch (saveError) {
      console.error('Error saving attendance rows:', saveError);
      setError('Could not save attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    const conducted = rows.reduce((sum, row) => sum + toInt(row.theoryConducted) + toInt(row.practicalConducted), 0);
    const attended = rows.reduce((sum, row) => sum + toInt(row.theoryAttended) + toInt(row.practicalAttended), 0);
    return {
      conducted,
      attended,
      percentage: percent(attended, conducted)
    };
  }, [rows]);

  if (loading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Loading attendance sheet...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Month</span>
          <select
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {MONTHS.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>

        <label className="lg:col-span-1">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Year</span>
          <input
            type="number"
            min="2020"
            max="2100"
            value={year}
            onChange={(event) => setYear(cleanInput(event.target.value))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">From</span>
          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </label>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">To</span>
          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </label>

        <div className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Division</span>
          <div className="flex items-center rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
            <Users className="mr-2 h-4 w-4 text-gray-500" />
            {assignedDivision || 'All'}
          </div>
        </div>

        <div className="flex items-end gap-2 lg:col-span-3">
          <button
            type="button"
            onClick={loadRows}
            className="inline-flex flex-1 items-center justify-center rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Load
          </button>
          <button
            type="button"
            onClick={saveRows}
            disabled={!canSave}
            className="inline-flex flex-1 items-center justify-center rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <label className="lg:col-span-4">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Search</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Roll number, name, or batch"
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </label>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Theory Held</span>
          <input
            type="number"
            min="0"
            value={bulkTheory}
            onChange={(event) => setBulkTheory(cleanInput(event.target.value))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Practical Held</span>
          <input
            type="number"
            min="0"
            value={bulkPractical}
            onChange={(event) => setBulkPractical(cleanInput(event.target.value))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <div className="flex items-end lg:col-span-2">
          <button
            type="button"
            onClick={applyConductedToAll}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Apply Held
          </button>
        </div>

        <div className="flex items-end lg:col-span-2">
          <div className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            <span className="font-semibold">{totals.percentage.toFixed(2)}%</span>
            <span className="ml-1 text-gray-500">overall</span>
          </div>
        </div>
      </div>

      {dateRangeInvalid && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          The start date must be before the end date.
        </div>
      )}

      {invalidRows.length > 0 && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          {invalidRows.length} row{invalidRows.length === 1 ? '' : 's'} have attended classes greater than held classes.
        </div>
      )}

      {error && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          {error}
        </div>
      )}

      {message && (
        <div className="flex items-center rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-[980px] w-full border-collapse text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="sticky left-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-left">Roll</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Student</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Batch</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Theory Held</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Theory Attended</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Practical Held</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Practical Attended</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Total</th>
              <th className="border-b border-gray-200 px-3 py-2 text-center">%</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-3 py-8 text-center text-gray-500">
                  No students found.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                const theoryConducted = toInt(row.theoryConducted);
                const theoryAttended = toInt(row.theoryAttended);
                const practicalConducted = toInt(row.practicalConducted);
                const practicalAttended = toInt(row.practicalAttended);
                const totalConducted = theoryConducted + practicalConducted;
                const totalAttended = theoryAttended + practicalAttended;
                const overall = percent(totalAttended, totalConducted);
                const rowInvalid = theoryAttended > theoryConducted || practicalAttended > practicalConducted;

                return (
                  <tr key={row.rollNumber} className={rowInvalid ? 'bg-red-50' : row.dirty ? 'bg-blue-50/50' : 'bg-white'}>
                    <td className="sticky left-0 z-10 border-r border-t border-gray-200 bg-inherit px-3 py-2 font-semibold text-gray-900">
                      {row.rollNumber}
                    </td>
                    <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-800">{row.name}</td>
                    <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-600">{row.batch || '-'}</td>
                    <td className="border-r border-t border-gray-200 px-2 py-1">
                      <input
                        type="number"
                        min="0"
                        value={row.theoryConducted}
                        onChange={(event) => updateRow(row.rollNumber, 'theoryConducted', event.target.value)}
                        className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-center focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="border-r border-t border-gray-200 px-2 py-1">
                      <input
                        type="number"
                        min="0"
                        value={row.theoryAttended}
                        onChange={(event) => updateRow(row.rollNumber, 'theoryAttended', event.target.value)}
                        className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-center focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="border-r border-t border-gray-200 px-2 py-1">
                      <input
                        type="number"
                        min="0"
                        value={row.practicalConducted}
                        onChange={(event) => updateRow(row.rollNumber, 'practicalConducted', event.target.value)}
                        className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-center focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="border-r border-t border-gray-200 px-2 py-1">
                      <input
                        type="number"
                        min="0"
                        value={row.practicalAttended}
                        onChange={(event) => updateRow(row.rollNumber, 'practicalAttended', event.target.value)}
                        className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-center focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="border-r border-t border-gray-200 px-3 py-2 text-center font-semibold text-gray-700">
                      {totalAttended}/{totalConducted}
                    </td>
                    <td className={`border-t border-gray-200 px-3 py-2 text-center font-semibold ${overall < 75 && totalConducted > 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {overall.toFixed(2)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceEntryWidget;
